import { useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { useLayoutData } from '../../../hooks/useLayoutContext';
import HostedDesktopLayout from '../../../layouts/DesktopLayout';
import HostedFaqModal from '../../../components/hosted/FaqModal';
import { useFaqModal } from '../../../hooks/useFaqModal';
import HostedInstallInstructionsModal from '../../../components/hosted/InstallInstructionsModal';
import HostedActionRow from '../../../components/hosted/ActionRow';
import HostedNeedHelpSection from '../../../components/hosted/NeedHelpSection';
import HostedQrCodeCard from '../../../components/hosted/QrCodeCard';
import { useBackOrCancel } from '../../../hooks/useBackOrCancel';
import { type AgentStartPageProps } from '../../../chulak/ChulakSession';

/** Subtitle keys: sharing, then issuance. */
const SUBTITLES = {
  redirect: {
    share: 'agentStart.redirectSubtitle',
    issuance: 'agentStart.issuanceRedirectSubtitle',
  },
  qr: { share: 'agentStart.subtitle', issuance: 'agentStart.issuanceSubtitle' },
};

/**
 * Continue in the app: a QR code to scan, or for apps that work through their
 * website, a button that goes there.
 */
export default function HostedDesktopAgentStartPage({
  tags,
  app,
  onCancel,
  onBack,
}: AgentStartPageProps) {
  const { t } = useTranslation();
  const appName = app?.nameWithArticle || app?.name || 'your identity app';
  const isIssuance = useLayoutData().requestType === 'issuance';
  const faq = useFaqModal();
  const [showInstall, setShowInstall] = useState(false);
  const backOrCancel = useBackOrCancel(onCancel, onBack);
  const view = tags.type === 'Redirect' ? 'redirect' : 'qr';

  return (
    <HostedDesktopLayout>
      <div className="flex flex-1 flex-col w-full">
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="flex flex-col items-center gap-6 text-center max-w-lg w-full">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {t('agentStart.title', { appName })}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t(SUBTITLES[view][isIssuance ? 'issuance' : 'share'], {
                  appName,
                })}
              </p>
            </div>

            {tags.type === 'Redirect' ? (
              <HostedActionRow
                className="my-6 w-full max-w-lg"
                primary={{
                  label: t('agentStart.redirectButton'),
                  onClick: () => window.location.replace(tags.redirect.uri),
                }}
                secondary={backOrCancel}
              />
            ) : (
              <>
                <HostedQrCodeCard
                  qrData={tags.type === 'Qr' ? tags.qr.content : ''}
                  appLogo={app?.logo}
                  appName={appName}
                />

                <button
                  className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
                  onClick={() => setShowInstall(true)}
                >
                  {t('agentStart.installInstructions')}
                </button>

                <HostedActionRow
                  className="my-6 w-full max-w-lg"
                  secondary={backOrCancel}
                />
              </>
            )}

            <HostedNeedHelpSection
              topics={
                view === 'redirect'
                  ? ['whyRedirection']
                  : ['whatAreWallets', 'howDataProtected', 'howObtainData']
              }
              onSelectTopic={faq.open}
            />
          </div>

          <HostedFaqModal {...faq.modal} />
          <HostedInstallInstructionsModal
            open={showInstall}
            appName={app?.name}
            appNameWithArticle={app?.nameWithArticle}
            onClose={() => setShowInstall(false)}
          />
        </div>
      </div>
    </HostedDesktopLayout>
  );
}
